import { useEffect, useRef, useState } from 'react';

export interface UseParallaxStickyProps {
  scrollContainerRef: React.RefObject<HTMLElement>;
  dataHookSelector: string;
  cssVarName: string;
  shouldEnable?: boolean;
  parallaxMultiplier?: number;
  maxTop: number;
  stickyPadding?: number;
}

export function useParallaxSticky({
  scrollContainerRef,
  dataHookSelector,
  cssVarName,
  shouldEnable = true,
  parallaxMultiplier = 0.5,
  maxTop,
  stickyPadding = 15,
}: UseParallaxStickyProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [initialTop, setInitialTop] = useState<number | null>(null);
  const isStickyRef = useRef<boolean>(false);

  const updateClassElementTop = (movableElement: HTMLElement, top: number) => {
    movableElement.style.setProperty(cssVarName, `${top}px`);
    movableElement.style.transition = 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
  };

  useEffect(() => {
    if (!shouldEnable) {
      return;
    }
    if (!scrollContainerRef.current) {
      return;
    }
    const movableElement = scrollContainerRef.current.querySelector(
      dataHookSelector,
    ) as HTMLElement;
    if (movableElement && initialTop === null) {
      setInitialTop(movableElement.offsetTop);
    }
  }, [shouldEnable, initialTop, scrollContainerRef, dataHookSelector]);

  useEffect(() => {
    if (!shouldEnable || isSticky) {
      return;
    }
    if (!scrollContainerRef.current || initialTop === null) {
      return;
    }
    const container = scrollContainerRef.current;
    const movableElement = container.querySelector(
      dataHookSelector,
    ) as HTMLElement;
    if (!movableElement) {
      return;
    }

    const handleScroll = () => {
      if (isSticky) {
        return;
      }
      const scrollTop = container.scrollTop;
      const newTop = initialTop + scrollTop * parallaxMultiplier;
      if (newTop > maxTop || isStickyRef.current) {
        return;
      }
      updateClassElementTop(movableElement, newTop);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    shouldEnable,
    isSticky,
    initialTop,
    scrollContainerRef,
    dataHookSelector,
    parallaxMultiplier,
    maxTop,
  ]);

  const setSticky = () => {
    if (!shouldEnable) {
      return;
    }
    setIsSticky(true);
    if (!scrollContainerRef.current) {
      return;
    }
    const movableElement = scrollContainerRef.current.querySelector(
      dataHookSelector,
    ) as HTMLElement;
    if (movableElement) {
      updateClassElementTop(movableElement, maxTop + stickyPadding);
      isStickyRef.current = true;
    }
  };

  const unsetSticky = () => {
    if (!shouldEnable) {
      return;
    }
    setIsSticky(false);
    if (!scrollContainerRef.current) {
      return;
    }
    const movableElement = scrollContainerRef.current.querySelector(
      dataHookSelector,
    ) as HTMLElement;
    if (movableElement) {
      updateClassElementTop(movableElement, maxTop);
      isStickyRef.current = false;
    }
  };

  return { setSticky, unsetSticky };
}
